import { useState } from "react";
import { SecondaryButton } from "@/components/admin/AdminUI";
import api from "@/lib/api";
import { toast } from "@/lib/toast";

/**
 * Triggers a CSV download by calling an admin export endpoint and turning the
 * response into a blob the browser saves to disk. Uses `api` (axios) so it
 * automatically attaches the admin JWT.
 *
 * The dropdown lets the editor pick `,` (default) or `;` (for non-US Excel).
 * The backend export endpoint must support the `?delimiter=` query param.
 */
export function ExportCsvButton({ endpoint, filename, label = "Export CSV", testid = "export-csv-button" }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function download(delimiter = ",") {
    setBusy(true);
    setOpen(false);
    try {
      const url = delimiter === ";" ? `${endpoint}?delimiter=%3B` : endpoint;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative inline-block">
      <SecondaryButton onClick={() => setOpen((v) => !v)} disabled={busy} data-testid={testid}>
        {busy ? "Exporting…" : `${label} ▾`}
      </SecondaryButton>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border border-line shadow-md rounded z-20 min-w-[180px]" data-testid={`${testid}-menu`}>
          <button
            type="button"
            onClick={() => download(",")}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F8F9FA]"
            data-testid={`${testid}-comma`}
          >
            Comma-separated (,)
          </button>
          <button
            type="button"
            onClick={() => download(";")}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F8F9FA] border-t border-line"
            data-testid={`${testid}-semicolon`}
          >
            Semicolon-separated (;)
          </button>
        </div>
      )}
    </div>
  );
}
